import { useQuery } from "@tanstack/react-query";
import type { SupabaseClient } from "@supabase/supabase-js";
import { useSupabaseClient } from "@/hooks/useSupabaseClient";
import type { WaitlistSnapshot } from "@/utils/orderSpyHistory";
import type { WaitlistOrder } from "@/utils/orderSpySort";

const SNAPSHOT_COLUMNS =
  "id, store_id, business_date, source, captured_at, order_count, earliest_promise_time, latest_promise_time, error";

/**
 * Reads the saved waitlist captures whose business_date falls in [fromDate,
 * toDate] (inclusive, both "YYYY-MM-DD"). Ordinary TanStack read (brief decision
 * 7); history is not a live POS call. Pages explicitly so PostgREST's max-rows
 * cap can never silently truncate a wide range (same reason as usePosStores).
 */
export const useWaitlistSnapshots = (fromDate: string, toDate: string) => {
  const getSupabaseClient = useSupabaseClient();

  return useQuery<WaitlistSnapshot[]>({
    queryKey: ["pos-waitlist-snapshots", fromDate, toDate],
    queryFn: async () => {
      const supabase = await getSupabaseClient();
      if (!supabase) throw new Error("Not authenticated");
      const sb = supabase as unknown as SupabaseClient;

      const PAGE = 1000;
      const rows: WaitlistSnapshot[] = [];
      for (let offset = 0; ; ) {
        const { data, error } = await sb
          .from("pos_waitlist_snapshots")
          .select(SNAPSHOT_COLUMNS)
          .gte("business_date", fromDate)
          .lte("business_date", toDate)
          .order("captured_at", { ascending: true })
          .order("id", { ascending: true })
          .range(offset, offset + PAGE - 1);
        if (error) throw error;

        const page = (data ?? []) as unknown as WaitlistSnapshot[];
        if (page.length === 0) break;
        rows.push(...page);
        if (page.length < PAGE) break;
        offset += page.length;
      }
      return rows;
    },
    staleTime: 60 * 1000,
  });
};

/**
 * Loads one capture's raw order records by id, only when a History cell is
 * opened — the grid query deliberately omits the `orders` jsonb.
 */
export const useWaitlistSnapshotOrders = (snapshotId: WaitlistSnapshot["id"] | null) => {
  const getSupabaseClient = useSupabaseClient();

  return useQuery<WaitlistOrder[]>({
    queryKey: ["pos-waitlist-snapshot-orders", snapshotId],
    enabled: snapshotId !== null,
    queryFn: async () => {
      const supabase = await getSupabaseClient();
      if (!supabase) throw new Error("Not authenticated");
      const sb = supabase as unknown as SupabaseClient;
      const { data, error } = await sb
        .from("pos_waitlist_snapshots")
        .select("orders")
        .eq("id", snapshotId)
        .single();
      if (error) throw error;
      const orders = (data as { orders?: unknown } | null)?.orders;
      return Array.isArray(orders) ? (orders as WaitlistOrder[]) : [];
    },
    staleTime: 5 * 60 * 1000,
  });
};
